import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface PanelPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PropertyFilter {
  type: 'category' | 'search' | 'component';
  value: string;
  active: boolean;
}

export interface UIState {
  layout: {
    mode: 'floating' | 'docked' | 'modal' | 'legacy';
    panelPositions: Record<string, PanelPosition>;
    collapsedPanels: string[];
    stickyActionsVisible: boolean;
  };
  inspector: {
    selectedComponent: string | null;
    activePropertyGroup: string;
    searchTerm: string;
    filterCriteria: PropertyFilter[];
    visible: boolean;
    position: PanelPosition | null;
  };
  workspace: {
    zoom: number;
    gridVisible: boolean;
    previewMode: boolean;
    screenSize: 'mobile' | 'tablet' | 'desktop' | 'large';
  };
}

const initialState: UIState = {
  layout: {
    mode: 'legacy',
    panelPositions: {},
    collapsedPanels: [],
    stickyActionsVisible: true,
  },
  inspector: {
    selectedComponent: null,
    activePropertyGroup: 'essential',
    searchTerm: '',
    filterCriteria: [],
    visible: true,
    position: null,
  },
  workspace: {
    zoom: 1,
    gridVisible: true,
    previewMode: false,
    screenSize: 'desktop',
  },
};

@Injectable({
  providedIn: 'root',
})
export class UIStateService {
  private state$ = new BehaviorSubject<UIState>(initialState);

  // Seletores reativos
  layout$ = this.state$.pipe(map(s => s.layout));
  inspector$ = this.state$.pipe(map(s => s.inspector));
  workspace$ = this.state$.pipe(map(s => s.workspace));

  // Seletores específicos
  selectedComponent$ = this.state$.pipe(map(s => s.inspector.selectedComponent));
  inspectorVisible$ = this.state$.pipe(map(s => s.inspector.visible));
  screenSize$ = this.state$.pipe(map(s => s.workspace.screenSize));
  searchTerm$ = this.state$.pipe(map(s => s.inspector.searchTerm));

  // Ações
  updateLayout(layout: Partial<UIState['layout']>) {
    this.state$.next({
      ...this.state$.value,
      layout: { ...this.state$.value.layout, ...layout },
    });
  }

  updateInspector(inspector: Partial<UIState['inspector']>) {
    this.state$.next({
      ...this.state$.value,
      inspector: { ...this.state$.value.inspector, ...inspector },
    });
  }

  updateWorkspace(workspace: Partial<UIState['workspace']>) {
    this.state$.next({
      ...this.state$.value,
      workspace: { ...this.state$.value.workspace, ...workspace },
    });
  }

  // Métodos de conveniência
  selectComponent(componentId: string | null) {
    this.updateInspector({
      selectedComponent: componentId,
      visible: componentId !== null,
    });
  }

  showInspector(position?: PanelPosition) {
    this.updateInspector({
      visible: true,
      position: position || null,
    });
  }

  hideInspector() {
    this.updateInspector({
      visible: false,
      selectedComponent: null,
    });
  }

  setSearchTerm(term: string) {
    this.updateInspector({ searchTerm: term });
  }

  togglePropertyGroup(groupId: string) {
    const currentCollapsed = this.state$.value.layout.collapsedPanels;
    const isCollapsed = currentCollapsed.includes(groupId);
    
    const newCollapsed = isCollapsed
      ? currentCollapsed.filter(id => id !== groupId)
      : [...currentCollapsed, groupId];

    this.updateLayout({ collapsedPanels: newCollapsed });
  }

  setActivePropertyGroup(groupId: string) {
    this.updateInspector({ activePropertyGroup: groupId });
  }

  updateScreenSize(size: UIState['workspace']['screenSize']) {
    this.updateWorkspace({ screenSize: size });
  }

  // Obtém estado atual de forma síncrona
  getCurrentState(): UIState {
    return this.state$.value;
  }
}
